if (!customElements.get('rotating-videos')) {
  customElements.define(
    'rotating-videos',
    class RotatingVideos extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest('.section-rotating-videos');
        this.allVideoItems = Array.from(this.querySelectorAll('.rotating-video-item'));
        this.videoItems = this.allVideoItems.filter(item => {
          const video = item.querySelector('video');
          return video && video.querySelector('source');
        });
        this.currentIndex = 0;
        this.rotationInterval = null;
        this.duration = parseInt(this.section?.dataset.rotationDuration || 3) * 1000;
        this.autoplayEnabled = this.section?.dataset.autoplay === 'true';
        this.popup = this.section.querySelector('.video-popup-overlay');
        this.popupVideo = this.popup?.querySelector('.video-popup-video');
        this.closeBtn = this.popup?.querySelector('.video-popup-close');
        this.isInitialLoad = true;
        this.popupOpenedVideoIndex = null;
        // 2d fix: the IntersectionObserver is the single owner of start/stop.
        this.isInView = false;
      }

      connectedCallback() {
        // 2d fix: no 2s all-or-nothing start timer; rotation only starts once the
        // observer reports the section in view (see below).

        const videoWrappers = this.querySelectorAll('.rotating-video-wrapper');
        videoWrappers.forEach((wrapper, index) => {
          wrapper.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.openPopup(index);
          });
          wrapper.style.cursor = 'pointer';
        });

        if (this.closeBtn) {
          this.closeBtn.addEventListener('click', () => this.closePopup());
        }

        if (this.popup) {
          this.popup.addEventListener('click', (e) => {
            if (e.target === this.popup) {
              this.closePopup();
            }
          });
        }

        const scrollContainer = this.querySelector('grid-list');
        if (scrollContainer) {
          let scrollTimeout;
          scrollContainer.addEventListener('scroll', () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
              this.syncToVisibleVideo();
            }, 150);
          });
        }

        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            this.isInView = entry.isIntersecting;
            if (entry.isIntersecting && this.autoplayEnabled) {
              this.startRotation();
            } else {
              this.pauseRotation();
            }
          });
        }, { threshold: 0.5 });

        observer.observe(this);
      }

      disconnectedCallback() {
        this.pauseRotation();
      }

      startRotation() {
        this.pauseRotation();
        
        if (this.videoItems.length === 0) return;
        if (!this.isInView) return; // only the observer may start playback

        const currentItem = this.videoItems[this.currentIndex];
        const video = currentItem?.querySelector('video');
        
        this.showVideo(this.currentIndex);

        if (this.videoItems.length > 1 && video) {
          const startTimer = () => {
            this.rotationInterval = setInterval(() => {
              this.nextVideo();
            }, this.duration);
          };

          if (video.paused) {
            video.addEventListener('play', startTimer, { once: true });
          } else {
            startTimer();
          }
        }
      }

      pauseRotation() {
        if (this.rotationInterval) {
          clearInterval(this.rotationInterval);
          this.rotationInterval = null;
        }
        
        this.videoItems.forEach(item => {
          const video = item.querySelector('video');
          if (video) {
            video.pause();
          }
        });
      }

      nextVideo() {
        this.isInitialLoad = false;
        this.currentIndex = (this.currentIndex + 1) % this.videoItems.length;
        this.showVideo(this.currentIndex);
      }

      syncToVisibleVideo() {
        if (this.videoItems.length === 0) return;

        const scrollContainer = this.querySelector('grid-list');
        if (!scrollContainer) return;

        let mostVisibleIndex = 0;
        let maxVisibility = 0;

        this.videoItems.forEach((item, index) => {
          const rect = item.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          
          const visibleWidth = Math.min(rect.right, containerRect.right) - Math.max(rect.left, containerRect.left);
          const visibility = Math.max(0, visibleWidth) / rect.width;

          if (visibility > maxVisibility) {
            maxVisibility = visibility;
            mostVisibleIndex = index;
          }
        });

        if (mostVisibleIndex !== this.currentIndex && maxVisibility > 0.5) {
          this.currentIndex = mostVisibleIndex;
          this.showVideo(this.currentIndex);
          
          if (this.autoplayEnabled) {
            this.startRotation();
          }
        }
      }

      showVideo(index) {
        this.videoItems.forEach((item, i) => {
          const video = item.querySelector('video');
          if (!video) return;

          if (i === index) {
            const gridList = this.querySelector('grid-list');
            if (gridList && gridList.classList.contains('swipe-mobile')) {
              const itemRect = item.getBoundingClientRect();
              const containerRect = gridList.getBoundingClientRect();
              
              const isVisible = itemRect.left >= containerRect.left && itemRect.right <= containerRect.right;
              
              if (!isVisible) {
                const scrollLeft = gridList.scrollLeft;
                const targetScroll = scrollLeft + (itemRect.left - containerRect.left) - (containerRect.width / 2) + (itemRect.width / 2);
                
                gridList.scrollTo({
                  left: targetScroll,
                  behavior: 'smooth'
                });
              }
            }

            // 2d fix: no currentTime reset before play() - on a preload="none" video it
            // forces a fetch; play() starts clip from the beginning on its own on first play.
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(error => {
                console.log('Autoplay prevented, waiting for user interaction:', error);
                document.addEventListener('click', () => {
                  video.play().catch(() => {});
                }, { once: true });
              });
            }
          } else {
            video.pause();
            if (video.readyState > 0) video.currentTime = 0;
          }
        });
        
        this.currentIndex = index;
      }

      openPopup(index) {
        const item = this.allVideoItems[index];
        if (!item) return;

        const thumbnailVideo = item.querySelector('video');
        if (!thumbnailVideo || !this.popupVideo || !this.popup) return;
        
        if (!thumbnailVideo.querySelector('source')) {
          return;
        }

        this.pauseRotation();
        this.popupOpenedVideoIndex = index;

        this.allVideoItems.forEach(item => {
          const video = item.querySelector('video');
          if (video) {
            video.pause();
            if (video.readyState > 0) video.currentTime = 0;
          }
        });

        const rotatingVideosContainer = this.querySelector('rotating-videos') || this;
        rotatingVideosContainer.classList.add('rotating-videos-hidden');

        this.popupVideo.innerHTML = '';
        const sources = thumbnailVideo.querySelectorAll('source');
        sources.forEach(source => {
          const newSource = document.createElement('source');
          newSource.src = source.src;
          newSource.type = source.type;
          this.popupVideo.appendChild(newSource);
        });

        this.popupVideo.load();
        this.popup.classList.add('active');
        this.popupVideo.play();

        document.body.style.overflow = 'hidden';
      }

      closePopup() {
        if (!this.popup || !this.popupVideo) return;

        this.popup.classList.remove('active');
        this.popupVideo.pause();
        this.popupVideo.currentTime = 0;

        document.body.style.overflow = '';

        const rotatingVideosContainer = this.querySelector('rotating-videos') || this;
        rotatingVideosContainer.classList.remove('rotating-videos-hidden');

        this.allVideoItems.forEach(item => {
          const video = item.querySelector('video');
          if (video) {
            video.pause();
            if (video.readyState > 0) video.currentTime = 0;
          }
        });

        if (this.videoItems.length > 0 && this.popupOpenedVideoIndex !== null) {
          const currentIndexInVideoItems = this.videoItems.findIndex(item => {
            return this.allVideoItems.indexOf(item) === this.popupOpenedVideoIndex;
          });
          
          if (currentIndexInVideoItems !== -1) {
            this.currentIndex = (currentIndexInVideoItems + 1) % this.videoItems.length;
          }
        }

        this.popupOpenedVideoIndex = null;

        if (this.autoplayEnabled) {
          this.startRotation();
        }
      }
    }
  );
}
